import { Dispatch, SetStateAction, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import useNotification, {
  Notification as NotificationInterface
} from 'commons/customHooks/useNotifications'
import { formatSendDate } from 'commons/utils/date'
import useOnClickOutside from 'commons/customHooks/useOnClickOutside'
import { URL_CONST } from 'constants/urls'

import * as S from './styles'
import { Icon } from 'components'
import { theme } from 'styles/theme/hav-theme'

export type NotificationType = 'success' | 'warning' | 'information' | 'fail' | 'error'
export enum Source {
  VOLUME_EXTRA = 'VOLUME_EXTRA',
  INTEGRACAO_EDI = 'INTEGRACAO_EDI',
  APROVACAO_ND = 'APROVACAO_ND',
  REPROVACAO_ND = 'REPROVACAO_ND',
  ANALISE = 'ANALISE',
  SOLICITACAO = 'SOLICITACAO',
  JUSTIFICAR_ND = 'JUSTIFICAR_ND',
  ND_EXPIRADA = 'ND_EXPIRADA',
  RETORNO_SAP = 'RETORNO_SAP'
}

type ComponentType = {
  setOpen: Dispatch<SetStateAction<boolean>>
  notifButtonRef?: React.RefObject<HTMLDivElement>
}
interface NotificationProps {
  source: Source
  title: string
  subtitle?: string
  message: string
  notes: string
  type: NotificationType
  read: boolean
  create_date: string
  handleClose: () => void
}
const Notification = ({
  create_date,
  message,
  read,
  source,
  title,
  subtitle,
  notes,
  type,
  handleClose
}: NotificationProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const navToSource = (source: Source, id: string) => {
    if (!id) return

    const getRoute = (source: Source) => {
      if ([Source.VOLUME_EXTRA, Source.INTEGRACAO_EDI].includes(source)) {
        return URL_CONST.MANAGE_ORDER
      }

      if ([Source.ANALISE, Source.SOLICITACAO].includes(source))
        return URL_CONST.BACKOFFICE

      if ([Source.JUSTIFICAR_ND, Source.ND_EXPIRADA, Source.RETORNO_SAP].includes(source))
        return URL_CONST.MANAGE_OV

      return URL_CONST.ROOT
    }

    const navRoute =
      source === Source.JUSTIFICAR_ND
        ? `${getRoute(source)}/${id}/justificar`
        : `${getRoute(source)}/${id}`

    if (location.pathname === navRoute) {
      navigate(0)
    } else {
      navigate(navRoute)
    }
    handleClose()
  }

  const parseBold = (text: string) => {
    const parts = text.split(/(<strong>.*?<\/strong>)/g)
    return parts.map((part) => {
      if (part.includes('<strong>')) {
        return <strong>{part.replace(/<\/?strong>/g, '')}</strong>
      } else {
        return part
      }
    })
  }

  return (
    <S.NotificationContainer
      hasNotes={Boolean(notes)}
      onClick={() => navToSource(source, notes)} // NOTE: Notes is the ID of ORDER or OV
    >
      <S.NotificationRow>
        <S.IconBox read={!read} type={type}>
          {type === 'success' && (
            <Icon icon="task-alt" color={theme.colors.alert.success.darker} size="15" />
          )}
          {type === 'warning' && (
            <Icon icon="report" color={theme.colors.alert.warning[50]} size="15" />
          )}
          {type === 'information' && (
            <Icon icon="info" color={theme.colors.alert.info[40]} size="15" />
          )}
          {['fail', 'error'].includes(type) && (
            <Icon icon="cancel" color={theme.colors.alert.danger.darker} size="15" />
          )}
        </S.IconBox>
        <S.TitleContainer>
          <S.NotificationTitleRow>
            <S.TitleNotif>{title}</S.TitleNotif>
            <S.SubTitleNotif>{formatSendDate(create_date)}</S.SubTitleNotif>
          </S.NotificationTitleRow>
          {subtitle && <S.SubTitleNotif>{subtitle}</S.SubTitleNotif>}
        </S.TitleContainer>
      </S.NotificationRow>
      <S.ContentNotif>{parseBold(message)}</S.ContentNotif>
    </S.NotificationContainer>
  )
}

export const Notifications: React.FC<ComponentType> = ({ setOpen, notifButtonRef }) => {
  const { notificationsData, processUnreadedNotifications } = useNotification()
  const notRef = useRef<HTMLDivElement>(null)

  const handleNotificationClose = () => {
    setOpen(false)
    processUnreadedNotifications()
  }
  useOnClickOutside(notRef, handleNotificationClose, notifButtonRef)

  const handleNotifSource = (notif: NotificationInterface) => {
    if (notif.source === Source.APROVACAO_ND)
      return {
        title: 'Negociação diferenciada aprovada',
        type: 'success' as NotificationType,
        notes: notif.notes
      }
    if (notif.source === Source.REPROVACAO_ND)
      return {
        title: 'Negociação diferenciada reprovada',
        type: 'fail' as NotificationType,
        notes: notif.notes
      }
    if ([Source.ANALISE, Source.SOLICITACAO].includes(notif.source))
      return {
        title: notif.title,
        type: notif.type,
        notes: 'alcadas'
      }
    return { title: notif.title, type: notif.type, notes: notif.notes }
  }
  return (
    <S.WrapContainerStyled ref={notRef}>
      <S.Title>Notificações</S.Title>

      {notificationsData?.length ? (
        <S.NotificationsList>
          {notificationsData?.map((not) => {
            const { title, type, notes } = handleNotifSource(not)
            return (
              <Notification
                key={not.notificationId}
                create_date={not.create_date}
                title={title}
                subtitle={not.subtitle}
                message={not.message}
                notes={notes}
                read={not.read}
                type={type}
                source={not.source}
                handleClose={handleNotificationClose}
              />
            )
          })}
        </S.NotificationsList>
      ) : (
        <S.Content>Nenhuma notificação recebida.</S.Content>
      )}
    </S.WrapContainerStyled>
  )
}
